import { useCallback, useRef, useState } from 'react';
import { uploadFileToS3 } from '@/shared/utils/uploadFileToS3';
import { CONTACT_REQUEST_LABELS as L } from '../constants/labels';
import { generateContactRequestAttachmentPresignedUrls } from '../api/contactRequestAttachmentPresignedApiService';
import { updateContactRequestStatus } from '../api/updateContactRequestStatusApiService';
import type { ContactRequestAttachmentMedia } from '../types/contactRequestTypes';

/** Max characters allowed in the completion note (matches backend). */
export const COMPLETE_CR_NOTES_MAX_LENGTH = 500;
/** Max number of attachments per completion (matches backend). */
export const COMPLETE_CR_MAX_FILES = 5;
/** Max size per attachment in MB (matches backend). */
export const COMPLETE_CR_MAX_FILE_SIZE_MB = 5;

interface UseCompleteContactRequestOptions {
  contactRequestId: string | undefined;
  /** Fired after a successful completion so the caller can refetch + snackbar. */
  onSuccess?: () => void;
}

export interface UseCompleteContactRequestReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;

  // File selection — files upload to S3 immediately on selection.
  files: File[];
  onFilesChange: (files: File[]) => void;
  /** True while one or more selected files are still uploading to S3. */
  isUploading: boolean;

  /** Single toast channel for file-validation, the notes/attachment rule, and upload failures. */
  toastMessage: string;
  showToast: (message: string) => void;
  clearToast: () => void;

  // Completion note
  notes: string;
  setNotes: (value: string) => void;
  notesError: string;

  // Submit
  isSubmitting: boolean;
  submitError: string;
  submit: () => Promise<void>;
}

/**
 * Placeholder S3 object key for a completion attachment. The backend resolves
 * the `entityid-placeholder` / `resourceid-placeholder` segments into the real
 * ids when the presigned URL is generated with `generatePath: true`.
 */
const buildAttachmentPath = (filename: string) =>
  `contact-requests/entityid-placeholder/attachments/resourceid-placeholder/${filename}`;

/** Pull the `{resourceId}` segment out of a resolved attachment path. */
const resourceIdFromPath = (path: string): string => path.split('/')[3] ?? '';

/**
 * Drives the "Complete & send response" modal for an assigned admin (RR-1097).
 *
 * Selected files are uploaded to S3 **immediately** (via the presigned-URL flow)
 * as they are added in the modal; removing a file drops its uploaded resource.
 * The final `submit()` therefore performs only the
 * `PATCH /contact-requests/{id}/status` call, sending the already-uploaded media
 * as the completion `response.attachments` together with the optional note.
 * Completing requires a note OR at least one attachment — enforced here
 * (surfaced as a toast) as well as by the backend.
 */
export const useCompleteContactRequest = ({
  contactRequestId,
  onSuccess,
}: UseCompleteContactRequestOptions): UseCompleteContactRequestReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [toastMessage, setToastMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const [notes, setNotesState] = useState('');
  const [notesError, setNotesError] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [submitError, setSubmitError] = useState('');

  // Refs mirror the latest values so the async upload flow (which may outlive a
  // given render) always reconciles against current state without stale reads.
  const filesRef = useRef<File[]>([]);
  const attachmentsRef = useRef<ContactRequestAttachmentMedia[]>([]);
  const uploadingNamesRef = useRef<Set<string>>(new Set());

  const syncUploading = useCallback(() => {
    setIsUploading(uploadingNamesRef.current.size > 0);
  }, []);

  const resetForm = useCallback(() => {
    filesRef.current = [];
    attachmentsRef.current = [];
    uploadingNamesRef.current = new Set();
    setFiles([]);
    setIsUploading(false);
    setToastMessage('');
    setNotesState('');
    setNotesError('');
    setSubmitError('');
  }, []);

  const open = useCallback(() => {
    resetForm();
    setIsOpen(true);
  }, [resetForm]);

  const close = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    setIsOpen(false);
    resetForm();
  }, [isSubmitting, resetForm]);

  const showToast = useCallback((message: string) => {
    setToastMessage(message);
  }, []);
  const clearToast = useCallback(() => setToastMessage(''), []);

  /**
   * Uploads a freshly-added batch of files to S3 and appends the resolved media
   * to `attachmentsRef`. Files whose upload fails are dropped from the selection
   * and a toast is shown. Media for files removed mid-upload is discarded.
   */
  const uploadNewFiles = useCallback(
    async (toUpload: File[]) => {
      try {
        const paths = toUpload.map((f) => buildAttachmentPath(f.name));
        const presign =
          await generateContactRequestAttachmentPresignedUrls(paths);
        const entries =
          presign.success && presign.data ? presign.data.files : [];

        const results = await Promise.all(
          toUpload.map(async (file, idx) => {
            const entry = entries[idx];
            if (!entry) {
              return { name: file.name, media: null };
            }
            try {
              await uploadFileToS3(entry.url, file);
              const media: ContactRequestAttachmentMedia = {
                isUploaded: true,
                path: entry.path,
                resourceId: entry.resourceId ?? resourceIdFromPath(entry.path),
                filename: file.name,
                fileType: file.type,
                fileSize: file.size,
              };
              return { name: file.name, media };
            } catch {
              return { name: file.name, media: null };
            }
          })
        );

        // Only keep media for files still selected (a file may have been
        // removed while its upload was in flight).
        const stillSelected = new Set(filesRef.current.map((f) => f.name));
        const succeeded = results
          .filter((r) => r.media && stillSelected.has(r.name))
          .map((r) => r.media as ContactRequestAttachmentMedia);
        if (succeeded.length > 0) {
          attachmentsRef.current = [...attachmentsRef.current, ...succeeded];
        }

        const failedNames = new Set(
          results.filter((r) => !r.media).map((r) => r.name)
        );
        if (failedNames.size > 0) {
          filesRef.current = filesRef.current.filter(
            (f) => !failedNames.has(f.name)
          );
          setFiles(filesRef.current);
          setToastMessage(L.COMPLETE_CR_ERROR_UPLOAD_FAILED);
        }
      } finally {
        toUpload.forEach((f) => uploadingNamesRef.current.delete(f.name));
        syncUploading();
      }
    },
    [syncUploading]
  );

  const onFilesChange = useCallback(
    (nextFiles: File[]) => {
      setToastMessage('');
      setSubmitError('');

      const nextNames = new Set(nextFiles.map((f) => f.name));

      // Drop uploaded media + in-flight tracking for files that were removed.
      attachmentsRef.current = attachmentsRef.current.filter((a) =>
        nextNames.has(a.filename)
      );
      uploadingNamesRef.current.forEach((name) => {
        if (!nextNames.has(name)) {
          uploadingNamesRef.current.delete(name);
        }
      });

      filesRef.current = nextFiles;
      setFiles(nextFiles);

      // Upload files that are newly added (not already uploaded / uploading).
      const alreadyHandled = new Set<string>([
        ...attachmentsRef.current.map((a) => a.filename),
        ...uploadingNamesRef.current,
      ]);
      const toUpload = nextFiles.filter((f) => !alreadyHandled.has(f.name));
      if (toUpload.length === 0) {
        syncUploading();
        return;
      }
      toUpload.forEach((f) => uploadingNamesRef.current.add(f.name));
      syncUploading();
      void uploadNewFiles(toUpload);
    },
    [syncUploading, uploadNewFiles]
  );

  const setNotes = useCallback((value: string) => {
    setNotesState(value);
    setNotesError('');
    setToastMessage('');
  }, []);

  const submit = useCallback(async () => {
    if (!contactRequestId || isSubmittingRef.current) {
      return;
    }
    // Never submit while an upload is still resolving — the button is disabled
    // for this, but guard here too in case submit is invoked programmatically.
    if (uploadingNamesRef.current.size > 0) {
      return;
    }

    const trimmedNotes = notes.trim();
    // The button is always enabled; the notes-or-attachment rule is enforced
    // here and surfaced as a toast rather than blocking the click.
    if (trimmedNotes.length === 0 && attachmentsRef.current.length === 0) {
      setToastMessage(L.COMPLETE_CR_ERROR_NOTES_OR_ATTACHMENT);
      return;
    }
    if (trimmedNotes.length > COMPLETE_CR_NOTES_MAX_LENGTH) {
      setNotesError(L.COMPLETE_CR_ERROR_NOTES_TOO_LONG);
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const result = await updateContactRequestStatus({
        contactRequestId,
        newStatus: 'completed',
        response: {
          notes: trimmedNotes.length > 0 ? trimmedNotes : null,
          attachments: attachmentsRef.current,
        },
      });
      if (result.success) {
        setIsOpen(false);
        resetForm();
        onSuccess?.();
      } else {
        setSubmitError(result.error ?? L.COMPLETE_CR_ERROR_UPLOAD_FAILED);
      }
    } catch {
      setSubmitError(L.COMPLETE_CR_ERROR_UPLOAD_FAILED);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [contactRequestId, notes, onSuccess, resetForm]);

  return {
    isOpen,
    open,
    close,
    files,
    onFilesChange,
    isUploading,
    toastMessage,
    showToast,
    clearToast,
    notes,
    setNotes,
    notesError,
    isSubmitting,
    submitError,
    submit,
  };
};
